import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import AccessControlRoute from "./components/AccessControlRoute";
import DashboardRoute from "./components/DashboardRoute";
import AppLayout from "./components/AppLayout";
import Login from "./pages/Login";
import Welcome from "./pages/Welcome";
import Quiz from "./pages/Quiz";
import Results from "./pages/Results";
import Admin from "./pages/Admin";
import AdminRetake from "./pages/AdminRetake";
import AccessControl from "./pages/AccessControl";
import Reports from "./pages/Reports";
import AdminAssessments from "./pages/AdminAssessments";
import Dashboard from "./pages/Dashboard";
import DashboardLeadership from "./pages/DashboardLeadership";
import DashboardOrganization from "./pages/DashboardOrganization";
import Rules from "./pages/Rules";
import TestRoles from "./pages/TestRoles";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

/* Pages wrapped with sidebar layout */
const WithLayout = ({ children }) => (
  <ProtectedRoute>
    <AppLayout>{children}</AppLayout>
  </ProtectedRoute>
);

/* Admin-only pages with sidebar layout (gated by 'admin' dashboard key + role) */
const AdminWithLayout = ({ children }) => (
  <AdminRoute>
    <AppLayout>{children}</AppLayout>
  </AdminRoute>
);

/* Access Control page — role-only gate so admins can always unlock themselves */
const AccessControlWithLayout = ({ children }) => (
  <AccessControlRoute>
    <AppLayout>{children}</AppLayout>
  </AccessControlRoute>
);

/* Dashboard pages — c_level, manager, admin */
const DashboardWithLayout = ({ children }) => (
  <DashboardRoute>
    <AppLayout>{children}</AppLayout>
  </DashboardRoute>
);

/* Fullscreen pages (no sidebar — quiz, rules) */
const FullScreen = ({ children }) => (
  <ProtectedRoute>{children}</ProtectedRoute>
);

export default function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/test/roles" element={<TestRoles />} />
            <Route path="/welcome" element={<WithLayout><Welcome /></WithLayout>} />
            <Route path="/dashboard" element={<DashboardWithLayout><Dashboard /></DashboardWithLayout>} />
            <Route path="/dashboard/leadership" element={<DashboardWithLayout><DashboardLeadership /></DashboardWithLayout>} />
            <Route path="/dashboard/organization" element={<DashboardWithLayout><DashboardOrganization /></DashboardWithLayout>} />
            <Route path="/admin" element={<AdminWithLayout><Admin /></AdminWithLayout>} />
            <Route path="/admin/retake" element={<AdminWithLayout><AdminRetake /></AdminWithLayout>} />
            <Route path="/admin/access" element={<AccessControlWithLayout><AccessControl /></AccessControlWithLayout>} />
            <Route path="/admin/assessments" element={<AdminWithLayout><AdminAssessments /></AdminWithLayout>} />
            {/* Reports is now admin/superadmin only. Non-admin leaders use
                the inline download icons on the Leadership tab. */}
            <Route path="/reports" element={<AccessControlWithLayout><Reports /></AccessControlWithLayout>} />
            <Route path="/results/:sessionId" element={<WithLayout><Results /></WithLayout>} />
            <Route path="/rules" element={<FullScreen><Rules /></FullScreen>} />
            <Route path="/quiz" element={<FullScreen><Quiz /></FullScreen>} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
    </GoogleOAuthProvider>
  );
}
